/*
 * Copyright (c) 2020 CalAmp Corp.  All Rights Reserved
 */
import React, {
  forwardRef,
  MouseEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef
} from 'react';
import IconButton from '@material-ui/core/IconButton';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';
import {makeStyles} from '@material-ui/core/styles';
import ChevronLeft from '@material-ui/icons/ChevronLeft';
import ChevronRight from '@material-ui/icons/ChevronRight';
import {getLanguage, translate} from '@ion/core/src/locale/locale';
import {GenericObject} from '@ion/core/src/types/entities';
import {ViewSubGroupsSwitch} from '@ion/dls/src/components/switch/ViewSubGroupsSwitch';
import {useMedia} from '@ion/dls/src/hooks/useMedia';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {DLSTheme, ViewSubGroupsProps} from '@ion/dls/src/types/types';
import {UsePaginationResult} from '@ion/query/src/hooks/usePagination';
import GoToPage, {PageConstraintsProps} from './GoToPage';
import {AgGridType} from './IonGrid';
import {getPageProxy} from './IonGridUtils';

export interface GridResult {
  [key: string]: any;
}

interface StyleProps {
  rowPerPageTextTransform?: any;
  loading?: boolean;
  hideSubGroups?: boolean;
  isDialogControl?: boolean;
  showPaginationState?: boolean;
};

export interface PaginationControlsType extends StyleProps, ViewSubGroupsProps {
  gridApi: AgGridType;
  pageSizes?: number[];
  pageSize: number;
  setPageSize?: Function;
  totalResultCount: number;
  onPageBack?: (event: MouseEvent<HTMLButtonElement>) => void;
  onPageForward?: (event: MouseEvent<HTMLButtonElement>) => void;
  onManualPaginationChange?: Function;
  paginationState?: {
    api?: AgGridType;
  };
  paging?: UsePaginationResult;
  isPrefetching?: boolean;
  disablePaginateControls?: boolean;
  setDisablePaginateControls?: Function;
  pageConstraintProps?: PageConstraintsProps;
};
// defines the pagination container styles ...
const useStyles = makeStyles((theme: DLSTheme) => ({
  paginationContainer : {
    display                        : 'flex',
    justifyContent                 : 'end',
    fontSize                       : '12px',
    gap                            : '0 16px',
    alignItems                     : 'center',
    height                         : '56px',
    backgroundColor                : dlsColor('white'),
    borderTop                      : `solid 1px ${dlsColor('grey3')}`,
    color                          : dlsColor('grey1'),
    marginBottom                   : ({isDialogControl}: StyleProps) => !isDialogControl ? '40px' : '0px',
    ...theme.dls.typography.body3,
    [theme.breakpoints.down('sm')] : {
      paddingLeft    : '17px',
      paddingRight   : '17px',
      flexWrap       : 'wrap',
      justifyContent : 'space-evenly',
      paddingTop     : '12px'
    }
  },
  paginationSubgroups : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },
  paginationPageSize : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },
  paginationPageSizeSelect : {
    '& div' : {
      '&:hover:not(.Mui-disabled)' : {
        '&:before' : {
          borderBottom : 'none'
        }
      },
      '&:before' : {
        borderBottom : 'none'
      },
      '&:after' : {
        borderBottom : 'none'
      }
    },
    [theme.breakpoints.down('sm')] : {
      '& select' : {
        paddingLeft  : '7px',
        paddingRight : '7px'
      }
    }
  },
  pageSizeOption : {
    padding   : '5px 8px !important',
    textAlign : 'center'
  },
  paginationPaging : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center',
    marginRight    : '4px'
  },
  paginationPageArrow : {
    [theme.breakpoints.down('sm')] : {
      '& button' : {
        paddingLeft  : '7px',
        paddingRight : '7px'
      }
    }
  },
  selectList : {
    padding : '5px !important'
  },
  paper : {
    minWidth : '75px !important',
    width    : '75px'
  },
  selectIcon : {
    top : 'calc(50% - 15px)'
  },
  select : {
    ...theme.dls?.typography?.pagination
  },
  paginationPageSizeText : {
    textTransform                  : ({rowPerPageTextTransform}: StyleProps) => rowPerPageTextTransform ?? 'uppercase',
    [theme.breakpoints.down('sm')] : {
      maxWidth     : '100px',
      overflow     : 'hidden',
      textOverflow : 'ellipsis',
      whiteSpace   : 'nowrap'
    }
  },
  pageSizeItem : {
    '&:hover' : {
      backgroundColor : dlsColor('paleBlue')
    }
  },
  selectedPageSize : {
    '&&' : {
      backgroundColor : dlsColor('paleBlue'),
      '&:hover'       : {
        backgroundColor : dlsColor('paleBlue')
      }
    }
  },
  navigation : {
    display                        : 'flex',
    justifyContent                 : 'center',
    alignItems                     : 'center',
    gap                            : '4px',
    [theme.breakpoints.down('sm')] : {
      justifyContent : 'space-between',
      width          : ({hideSubGroups}: StyleProps) => hideSubGroups ? '100%' : 'auto'
    }
  },
  topRow : {
    display                        : 'flex',
    justifyContent                 : 'space-between',
    alignItems                     : 'center',
    gap                            : '12px',
    [theme.breakpoints.down('sm')] : {
      width : '100%'
    }
  },
  bottomRow : {
    minWidth                       : '171px',
    display                        : 'flex',
    justifyContent                 : 'space-between',
    flexWrap                       : 'nowrap',
    [theme.breakpoints.down('sm')] : {
      width : '100%'
    }

  }
}));

/**
 * The utility function returns the paginatation info object which includes page size,
 * total page, current page, next/previous availability. These information derived from
 * the React-Query paging mechanism.
 *
 * ```typescript
 *
 *  import {getDefaultPagingState} from '@ion/dls/src/grid/';
 *
 *  const {
 *    pageSize,
 *    hasNext,
 *    hasPrevious,
 *    topDisplayedRowIndex,
 *    bottomDisplayedRowIndex
 *  } = getDefaultPagingState(paging);
 *
 * ```
 */
export const getDefaultPagingState = (paging: UsePaginationResult) => {
  const {
    pageSize,
    hasPrevious,
    hasNext,
    total,
    page
  } = paging;
  const lastRow = (page * pageSize) - 1;

  return {
    active                  : false,
    topDisplayedRowIndex    : ((page - 1) * pageSize),
    bottomDisplayedRowIndex : lastRow < total ? lastRow : total - 1,
    totalPage               : Math.ceil(total / pageSize),
    pageSize,
    hasNext,
    hasPrevious
  };
};

/**
 * The utility function returns the paginatation info object which includes page size,
 * total page, current page, next/previous availability. These information derived from
 * the React-Query paging mechanism.
 *
 * ```typescript
 *
 *  import {getLegacyPagingState} from '@ion/dls/src/grid/';
 *
 *  const {
 *    pageSize,
 *    hasNext,
 *    hasPrevious,
 *    topDisplayedRowIndex,
 *    bottomDisplayedRowIndex
 *  } = getDefaultPagingState(paging, totalCount);
 *
 * ```
 */
export const getLegacyPagingState = (paging: GenericObject, totalResultCount: number) => {
  const {
    currentPage = 0,
    pageSize = 20,
    bottomDisplayedRowIndex = 0,
    topDisplayedRowIndex = 0
  } = getPageProxy(paging, totalResultCount);
  const totalPage = Math.ceil(totalResultCount / pageSize);

  return {
    active      : (totalPage === 0),
    hasNext     : !((currentPage + 1) === totalPage) || (totalResultCount === 0),
    hasPrevious : !(currentPage === 0),
    pageSize,
    totalPage,
    bottomDisplayedRowIndex,
    topDisplayedRowIndex
  };
};

/**
 * @PaginationControls
 *
 * Defines the custom pagination bar that is attached to the IonGrid if the user enables it
 *
 * @param {Array<number>} pageSizes the options in the select menu to
 *   set the page size
 * @param {object} gridApi object that contains the grid's api
 * @param {object} paginationState Hook function that sets the values of the gridRows
 * @param {Function} setPageSize function that sets the state with the page size
 * @param {Function} onPageBack function that gets called conditionally
 *   if passed when user pages back
 * @param {Function} onPageForward function that gets called conditionally
 *   if passed when user pages forward
 * @param {number} totalResultCount the total number of assets returned for this account,
 * regardless of page size
 * @param {boolean} viewSubGroups value that determines if subgroups should be fetched and shown
 * @param {Function} toggleViewSubGroups Hook function that sets the value of viewSubGroups
 *
 *
 * ```typescript
 *
 *   <PaginationControls gridApi={gridApi}
 *     gridRows={gridRows}
 *     setGridRows={setGridRows}
 *     paginationState={paginationState}
 *     totalResultCount={totalResultCount}
 *     setTotalResultCount={setTotalResultCount}
 *     getItems={getItems}
 *     setPageSize={setPageSize}
 *     gridPaginationSizes={gridPaginationSizes}
 *     loadItems={loadItems} />
 *
 * ```
 *
 */
export const PaginationControls = forwardRef(({
  gridApi,
  pageSizes,
  pageSize,
  setPageSize,
  totalResultCount,
  onPageBack,
  onPageForward,
  paginationState,
  viewSubGroups,
  toggleViewSubGroups,
  onManualPaginationChange,
  paging,
  disablePaginateControls,
  setDisablePaginateControls,
  pageConstraintProps,
  loading = false,
  isPrefetching = false,
  hideSubGroups = false,
  isDialogControl = false,
  showPaginationState = true,
  rowPerPageTextTransform = 'uppercase'
}: PaginationControlsType, ref) => {
  const classes   = useStyles({
    hideSubGroups : hideSubGroups || !toggleViewSubGroups,
    rowPerPageTextTransform,
    isDialogControl
  });
  const {isPhone} = useMedia();
  const isDisabled = useRef(false);

  useEffect(() => {
    if (!loading) {
      setDisablePaginateControls?.(false);
    }
  }, [loading]);

  const setPaginationEnabledState = useCallback((disabled = false) => {
    isDisabled.current = disabled;
  }, []);

  useImperativeHandle(ref, () => ({
    setPaginationEnabledState
  }), []);

  const {
    active,
    hasNext,
    totalPage,
    hasPrevious,
    bottomDisplayedRowIndex,
    topDisplayedRowIndex
  } = useMemo(() => {
    if (paging) {
      return getDefaultPagingState(paging);
    }
    return gridApi ? getLegacyPagingState(gridApi, totalResultCount) : {} as any;
  }, [paging, paginationState, gridApi, totalResultCount]);

  const toggleSubGroupsView = useCallback(() => {
    toggleViewSubGroups?.(!viewSubGroups);
  }, [viewSubGroups]);

  /**
   * @handlePageSizeChange
   *
   * If we already have the data we can simply change the page
   * size. If not, we need to get the row items we need.
   */
  async function handlePageSizeChange({target: {value}}) {
    setPageSize(value);
    if (paging) {
      const {gotoPage, setPageSize} = paging;
      gotoPage(1);
      setPageSize(value);
    }
    onManualPaginationChange?.('pageSizeChange');
  }

  /**
   * @handlePageBack
   *
   * Since we don't need to fetch data we can simply execute
   * a page back.
   */
  function handlePageBack(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    gridApi.paginationGoToPreviousPage();
    onPageBack?.(e);
    setPaginationEnabledState(false);
    setDisablePaginateControls?.(true);
    paging?.previousPage();
    onManualPaginationChange?.();
  }

  /**
   * @handlePageForward
   *
   * When user signals to move forward calling the gridAPi to
   * paginate forward will automatically check if the cache has the
   * data and if not will fetch it
   */
  function handlePageForward(e: MouseEvent<HTMLButtonElement>) {
    e.stopPropagation();
    gridApi.paginationGoToNextPage();
    onPageForward?.(e);
    setPaginationEnabledState(false);
    setDisablePaginateControls?.(true);
    paging?.nextPage();
    onManualPaginationChange?.();
  };

  const renderPageValue = useCallback(
    (value: string) => translate('grids:pagination.rowsPerPage', {rows: value}
    ), [getLanguage(), translate]);

  const subGroupToggle = useMemo(() => (
    <>
      {!hideSubGroups && toggleViewSubGroups && (
        <div className={classes.paginationSubgroups}>
          <ViewSubGroupsSwitch checked={viewSubGroups}
            onChange={toggleSubGroupsView}
            labelColor={dlsColor('grey2')}
            showLabel />
        </div>
      )}
    </>
  ), [
    hideSubGroups,
    toggleViewSubGroups,
    toggleSubGroupsView,
    viewSubGroups
  ]);

  return (
    <div className={classes.paginationContainer}>
      <div className={classes.topRow}>
        {showPaginationState && (
          <div>
            {translate('grids:pagination.currentState', {
              firstRow : totalResultCount ? topDisplayedRowIndex + 1 : 0,
              lastRow  : totalResultCount ? bottomDisplayedRowIndex + 1 : 0,
              pageSize : totalResultCount
            })}
          </div>
        )}
        {!isPhone && (subGroupToggle)}
        <div className={classes.paginationPageSize}>
          <div className={classes.paginationPageSizeSelect}>
            <Select value={pageSize}
              renderValue={renderPageValue}
              classes={{
                icon   : classes.selectIcon,
                select : classes.select
              }}
              MenuProps={{
                classes : {
                  list  : classes.selectList,
                  paper : classes.paper
                }
              }}
              onChange={handlePageSizeChange}>
              {pageSizes.map((val, i) => {
                return (
                  <MenuItem value={val}
                    key={`grid-rows-option-${val}`}
                    classes={{
                      root     : classes.pageSizeOption,
                      selected : classes.selectedPageSize,
                      gutters  : classes.pageSizeItem
                    }}>
                    {val}
                  </MenuItem>
                );
              })}
            </Select>
          </div>
        </div>
      </div>
      <div className={classes.bottomRow}>
        <div className={classes.navigation}>
          <div className={classes.paginationPageSizeText}>
            <GoToPage pageSize={pageSize}
              gridApi={gridApi}
              startRow={topDisplayedRowIndex}
              totalPage={totalPage}
              totalResultCount={totalResultCount}
              pageConstraintProps={pageConstraintProps}
              onManualPaginationChange={onManualPaginationChange}
              paging={paging}
              loading={loading} />
          </div>
          <div className={classes.paginationPaging}>
            <div className={classes.paginationPageArrow}>
              <IconButton onClick={handlePageBack}
                disabled={!hasPrevious || isPrefetching || disablePaginateControls}>
                <ChevronLeft />
              </IconButton>
            </div>
            <div className={classes.paginationPageArrow} style={{marginLeft: '-18px'}}>
              <IconButton onClick={handlePageForward}
                disabled={!hasNext || isPrefetching || active || disablePaginateControls}>
                <ChevronRight />
              </IconButton>
            </div>
          </div>
        </div>
        {isPhone && (subGroupToggle)}
      </div>
    </div>
  );
});

export default PaginationControls;
